// Fixes DEP-183 ("Bug when I switch tabs"): switching tabs or loading some
// pages (e.g. Home -> Books -> book analysis) hit a "Something went wrong"
// crash that required a manual reload.
//
// Detects module-chunk load failures — the classic "stale deploy" symptom:
// a tab still has an old index.html referencing a JS chunk hash that a
// newer deploy has since removed from the server, so a `React.lazy()`
// dynamic import() (or a <link rel="modulepreload">) 404s. Left unhandled,
// that throws all the way up to the top-level ErrorBoundary and strands the
// user on the crash screen for something a reload trivially fixes.
//
// Guarded by sessionStorage: if we already reloaded once recently and are
// still hitting a chunk error, something else is actually broken (real
// outage) — stop and let the caller fall back to the normal error UI
// instead of reload-looping forever.

import { useGlobalState } from '@/stores/globalState'
import type { AppModule, WorkspaceView } from '@/types'

const RELOAD_GUARD_KEY = 'hs-chunk-reload-at'
const RELOAD_GUARD_WINDOW_MS = 10_000
const RELOAD_NAV_INTENT_KEY = 'hs-chunk-reload-nav-intent'
const RELOAD_NAV_INTENT_MAX_AGE_MS = 60_000

const CHUNK_ERROR_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk [\w-]+ failed/i

interface ChunkReloadNavigationIntent {
  activeModule: AppModule
  workspaceView: WorkspaceView
  activeWorkspaceItem: string | null
  selectedBookId: string | null
  savedAt: number
}

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return CHUNK_ERROR_PATTERN.test(message)
}

function rememberChunkReloadNavigationIntent() {
  const state = useGlobalState.getState()
  const isDefaultHome =
    state.activeModule === 'MY_GUIDES' &&
    state.workspaceView === 'GALLERY' &&
    !state.activeWorkspaceItem &&
    !state.selectedBookId

  if (isDefaultHome) return

  const intent: ChunkReloadNavigationIntent = {
    activeModule: state.activeModule,
    workspaceView: state.workspaceView,
    activeWorkspaceItem: state.activeWorkspaceItem,
    selectedBookId: state.selectedBookId,
    savedAt: Date.now(),
  }

  try {
    sessionStorage.setItem(RELOAD_NAV_INTENT_KEY, JSON.stringify(intent))
  } catch {
    /* ignore */
  }
}

export function consumeChunkReloadNavigationIntent(): ChunkReloadNavigationIntent | null {
  let stored: string | null = null
  try {
    stored = sessionStorage.getItem(RELOAD_NAV_INTENT_KEY)
    sessionStorage.removeItem(RELOAD_NAV_INTENT_KEY)
  } catch {
    return null
  }

  if (!stored) return null

  try {
    const intent = JSON.parse(stored) as Partial<ChunkReloadNavigationIntent>
    if (
      typeof intent.savedAt !== 'number' ||
      Date.now() - intent.savedAt > RELOAD_NAV_INTENT_MAX_AGE_MS ||
      typeof intent.activeModule !== 'string' ||
      typeof intent.workspaceView !== 'string'
    ) {
      return null
    }

    return {
      activeModule: intent.activeModule as AppModule,
      workspaceView: intent.workspaceView as WorkspaceView,
      activeWorkspaceItem: intent.activeWorkspaceItem ?? null,
      selectedBookId: intent.selectedBookId ?? null,
      savedAt: intent.savedAt,
    }
  } catch {
    return null
  }
}

// Reloads once to pick up the fresh index.html (and therefore the current,
// valid chunk hashes). Returns true if a reload was triggered; false if a
// reload already happened within the guard window, meaning the caller
// should show the normal fallback UI instead of looping.
export function recoverFromChunkLoadError(): boolean {
  let lastAttempt = 0
  try {
    lastAttempt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0)
  } catch {
    // sessionStorage unavailable (private mode, etc.) — just attempt once.
  }

  if (Date.now() - lastAttempt < RELOAD_GUARD_WINDOW_MS) {
    return false
  }

  rememberChunkReloadNavigationIntent()

  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    /* ignore — worst case we reload more than once */
  }

  window.location.reload()
  return true
}
